import { request } from '@umijs/max';
import { AUTH_RESULT_CODES } from '@/constants/authResultCodes';
import { AuthBizError } from '@/utils/authBizError';
import { clearMemberWorkspaceCache } from '@/utils/memberWorkspaceCache';

const STORAGE_KEY = 'oauth_token_bundle';
const EXPIRY_SKEW_MS = 30_000;

export type TokenBundle = {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  scope?: string;
  expiresAt: number;
  refreshExpiresAt?: number;
};

export type OAuthTokenResponse = {
  access_token?: string;
  refresh_token?: string;
  refresh_expires_in?: number;
  scope?: string;
  token_type?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
};

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function loadTokenBundle(): TokenBundle | null {
  if (typeof window === 'undefined') return null;
  return safeParse<TokenBundle>(window.localStorage.getItem(STORAGE_KEY));
}

export function clearTokenBundle() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem('token');
  clearMemberWorkspaceCache();
}

export function saveTokenBundle(bundle: TokenBundle) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bundle));
  window.localStorage.setItem('token', bundle.accessToken);
}

export function saveTokenFromOAuthResponse(res: OAuthTokenResponse) {
  const accessToken = res?.access_token;
  if (!accessToken) return;

  const expiresInSec = Number(res?.expires_in ?? 0);
  const refreshExpiresInSec = Number(res?.refresh_expires_in ?? 0);
  const expiresAt = Date.now() + Math.max(0, expiresInSec * 1000 - EXPIRY_SKEW_MS);
  const refreshExpiresAt =
    refreshExpiresInSec > 0
      ? Date.now() + Math.max(0, refreshExpiresInSec * 1000 - EXPIRY_SKEW_MS)
      : undefined;

  saveTokenBundle({
    accessToken,
    refreshToken: res?.refresh_token,
    tokenType: res?.token_type,
    scope: res?.scope,
    expiresAt,
    refreshExpiresAt,
  });
}

export function getAccessToken(): string | null {
  const bundle = loadTokenBundle();
  if (!bundle?.accessToken) return null;
  if (bundle.expiresAt <= Date.now()) return null;
  return bundle.accessToken;
}

/** 是否存在未过期的 accessToken（必要时尝试 refresh） */
export async function hasValidSession(): Promise<boolean> {
  const token = await refreshAccessTokenIfNeeded();
  return Boolean(token);
}

export function isExpiringSoon(thresholdMs = 60_000): boolean {
  const bundle = loadTokenBundle();
  if (!bundle?.accessToken) return true;
  return bundle.expiresAt - Date.now() <= thresholdMs;
}

function getBasicAuthHeader(): string {
  if (typeof window === 'undefined') return '';
  return `Basic ${window.btoa(`${OAUTH_CLIENT_ID}:${OAUTH_CLIENT_SECRET}`)}`;
}

let refreshPromise: Promise<string | null> | null = null;

export async function refreshAccessTokenIfNeeded(): Promise<string | null> {
  const bundle = loadTokenBundle();
  if (!bundle?.accessToken) return null;
  if (!bundle.refreshToken) return bundle.accessToken;
  if (!isExpiringSoon()) return bundle.accessToken;

  if (bundle.refreshExpiresAt && bundle.refreshExpiresAt <= Date.now()) {
    clearTokenBundle();
    return null;
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const params = new URLSearchParams();
        params.set('grant_type', 'refresh_token');
        params.set('refresh_token', bundle.refreshToken || '');
        params.set('scope', bundle.scope || 'member');

        const res = await request<OAuthTokenResponse>('/oauth2/token', {
          method: 'POST',
          skipErrorHandler: true,
          headers: {
            Authorization: getBasicAuthHeader(),
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          data: params.toString(),
        });

        if (!res?.access_token) {
          clearTokenBundle();
          return null;
        }
        saveTokenFromOAuthResponse(res);
        return res.access_token;
      } catch {
        clearTokenBundle();
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
}

type OAuthErrorBody = {
  code?: number;
  message?: string;
  error?: string;
  error_description?: string;
};

async function requestOAuthToken(params: URLSearchParams): Promise<OAuthTokenResponse> {
  try {
    const res = await request<OAuthTokenResponse & OAuthErrorBody>('/oauth2/token', {
      method: 'POST',
      skipErrorHandler: true,
      headers: {
        Authorization: getBasicAuthHeader(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: params.toString(),
    });

    if (!res?.access_token) {
      const code =
        typeof res?.code === 'number' ? res.code : AUTH_RESULT_CODES.LOGIN_FAILED;
      throw new AuthBizError(code);
    }

    saveTokenFromOAuthResponse(res);
    return res;
  } catch (error: unknown) {
    if (error instanceof AuthBizError) {
      throw error;
    }
    const data = (error as { response?: { data?: OAuthErrorBody } })?.response?.data;
    if (typeof data?.code === 'number') {
      throw new AuthBizError(data.code);
    }
    throw new AuthBizError(AUTH_RESULT_CODES.LOGIN_FAILED);
  }
}

/** 账号密码登录 OAuth2 password grant */
export async function loginByPassword(username: string, password: string) {
  const params = new URLSearchParams();
  params.set('grant_type', 'password');
  params.set('username', username);
  params.set('password', password);
  params.set('scope', 'member');
  return requestOAuthToken(params);
}

/** 邮箱验证码登录 OAuth2 email_code grant */
export async function loginByEmailCode(email: string, code: string) {
  const params = new URLSearchParams();
  params.set('grant_type', 'email_code');
  params.set('email', email.trim());
  params.set('code', code.trim());
  params.set('scope', 'member');
  return requestOAuthToken(params);
}
